/* 3D-Ansicht mit three.js: texturierte Darstellung, Auswahl per Klick, Orbit- und Flugmodus.
 * Fällt ohne three.js (oder ohne WebGL) auf einen Hinweistext zurück, der Rest des Editors läuft weiter. */
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import Cmd from './model/cmds.ts';
import Core from './core.ts';
import Doors from './model/doors.ts';
import EntDefs from './model/entdefs.ts';
import { boxOf, DROP_MAX, dropsToFloor, isFunctionBrush, modelAnchor, modelPathOf } from './model/entvisual.ts';
import { geometryBounds } from '@xen-forge/mdl';
import { faceNormal, originOnSurface } from './model/entplace.ts';
import I18n from './i18n.ts';
import Light from './analysis/light.ts';
import { pickDir } from './model/sculpt.ts';
import { skyVisible } from './model/assets.ts';
import { ceilingMask } from './model/ceilings.ts';
import { isSkyFace } from './model/skyview.ts';
const View3D = (function (Core, Cmd, EntDefs, Light, Doors) {
  'use strict';
  const _ = I18n.t;

  const SPECIAL_COL = {
    SKY: [0.55, 0.75, 1, 1], CLIP: [0.7, 0.3, 1, 0.35], NULL: [0.55, 0.55, 0.55, 1], SKIP: [0.4, 0.4, 0.4, 0.3], HINT: [0.3, 0.9, 0.9, 0.3],
    SOLIDHINT: [0.3, 0.9, 0.9, 0.3], ORIGIN: [0.9, 0.3, 0.3, 0.5], AAATRIGGER: [1, 0.6, 0.1, 0.35], BEVEL: [0.5, 0.5, 0.5, 0.3],
    BOUNDINGBOX: [0.5, 0.5, 0.5, 0.2], CONTENTWATER: [0.2, 0.4, 0.9, 0.4], NOCLIP: [0.7, 0.3, 1, 0.35]
  };
  const hashCol = (s) => {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    const a = ((h >>> 0) % 360) / 360, sat = 0.28 + ((h >>> 9) % 20) / 100, l = 0.5 + ((h >>> 17) % 20) / 100;
    const f = (n) => { const k = (n + a * 12) % 12; return l - sat * Math.min(l, 1 - l) * Math.max(-1, Math.min(k - 3, 9 - k, 1)); };
    return [f(0), f(8), f(4)];
  };
  const T = (p) => new THREE.Vector3(p[0], p[2], -p[1]);
  const LIGHT = Core.norm([0.35, 0.45, 0.82]);
  const SKY_COL = {
    desert: '#a9c4de', '2desert': '#b5c2d0', city: '#8f9bab', cliff: '#86a2bf', day: '#8fb8e0', dusk: '#cc8f70', morning: '#b6c9dc', night: '#10162b',
    neb1: '#1d1233', space: '#05060a', black: '#000000', alien1: '#44502f', alien2: '#44502f', alien3: '#44502f', xen8: '#523a5e', default: '#8aa4bf'
  };
  const rgbOf = (hex) => [parseInt(hex.substr(1, 2), 16) / 255, parseInt(hex.substr(3, 2), 16) / 255, parseInt(hex.substr(5, 2), 16) / 255];
  const cssRgb = (c) => 'rgb(' + Math.round(c[0] * 255) + ',' + Math.round(c[1] * 255) + ',' + Math.round(c[2] * 255) + ')';

  class View3D {
    constructor(app, host) {
      this.app = app; this.host = host; this.key = 'persp'; this.ok = false;
      this.doc = null; this.bsp = null; this.bspMeshes = []; this.bspLit = true; this.meshes = []; this.entMeshes = []; this.texCache = new Map();
      this.lm = null; this.lmTex = null; this.bspGamma = true; this.lit = false; this.exposure = 1; this.showPlayer = false; this.lmOnly = false; this.skyRGB = null; this.hideSky = false; this.hideCeil = false;
      this.fly = false; this.keys = new Set(); this.flySpeed = 400; this.yaw = 0; this.pitch = 0;
      this.dirty = { geo: true, sel: true }; this.pending = false; this.last = 0; this.down = null;
      try {
        // Farben wie bisher unverändert durchreichen (three ≥ r152 rechnet sonst nach sRGB um und die Ansicht sähe anders aus)
        THREE.ColorManagement.enabled = false;
        this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
        this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
      } catch (err) { this.fail(_('v3.no_webgl')); return; }
      this.canvas = this.renderer.domElement;
      this.canvas.style.cssText = 'display:block;width:100%;height:100%;touch-action:none';
      host.appendChild(this.canvas);
      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(60, 1, 2, 60000);
      this.camera.position.set(900, 700, 900);
      this.world = new THREE.Group(); this.overlay = new THREE.Group(); this.ents = new THREE.Group();
      this.scene.add(this.world, this.overlay, this.ents);
      this.grid = null;
      {
        this.controls = new OrbitControls(this.camera, this.canvas);
        this.controls.enableDamping = false;
        this.controls.addEventListener('change', () => { this.busy(); this.invalidate(); });
      }
      this.ray = new THREE.Raycaster();
      this.bindInput();
      this.ok = true;
      this.setTheme();
    }

    fail(msg) {
      const d = document.createElement('div');
      d.className = 'vp-note'; d.textContent = msg;
      this.host.appendChild(d);
      this.invalidate = () => {}; this.resize = () => {};
    }

    setTheme() {
      if (!this.ok) return;
      const C = this.app.colors();
      this.scene.background = new THREE.Color(C.bg); this.dirty.geo = true;
      if (this.grid) { this.scene.remove(this.grid); this.grid.geometry.dispose(); }
      this.grid = new THREE.GridHelper(8192, 64, new THREE.Color(C.axis), new THREE.Color(C.gridMajor));
      this.grid.material.transparent = true; this.grid.material.opacity = 0.5;
      this.scene.add(this.grid);
      this.invalidate();
    }

    // ------------------------------------------------------------ Daten
    setDoc(doc) { this.doc = this.app.filtered ? this.app.filtered(doc) : doc; this.dirty.geo = true; this.dirty.sel = true; this.invalidate(); }
    setSel() { this.dirty.sel = true; this.invalidate(); }
    /** BSP-Szene (Bsp.scene) setzen oder null. Die Lightmaps der BSP kommen als Atlas. */
    setBsp(sc) {
      if (!this.ok) return;
      if (this.lmTex) { this.lmTex.dispose(); this.lmTex = null; }
      this.bsp = sc; this.lm = null;
      if (sc && sc.atlas) {
        const c = document.createElement('canvas'); c.width = sc.atlas.W; c.height = sc.atlas.H;
        let d = sc.atlas.data;
        if (this.bspGamma) { // Näherung der Engine-Aufhellung: dunkle Lightmap-Werte werden angehoben
          d = new Uint8ClampedArray(sc.atlas.data);
          const lut = new Uint8Array(256); for (let i = 0; i < 256; i++) lut[i] = Math.round(255 * Math.pow(i / 255, 0.5));
          for (let i = 0; i < d.length; i += 4) { d[i] = lut[d[i]]; d[i + 1] = lut[d[i + 1]]; d[i + 2] = lut[d[i + 2]]; }
        }
        c.getContext('2d').putImageData(new ImageData(d, sc.atlas.W, sc.atlas.H), 0, 0);
        const t = new THREE.CanvasTexture(c);
        t.flipY = false; t.channel = 1; t.generateMipmaps = false; t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
        this.lmTex = t;
      }
      this.bspLit = true; this.lit = !!(sc && this.lmTex);
      this.texturesChanged();
    }
    /** Skybox wurde geladen/umgeschaltet: Hintergrund und Sky-Flächen neu aufbauen. */
    updateSky() { if (this.skyMesh) { this.skyMesh.position.copy(this.camera.position); const k = this.camera.far * 0.5; this.skyMesh.scale.set(k, k, k); } }
    skyboxChanged() { if (this.bsp) this.setBsp(this.bsp); this.dirty.geo = true; this.invalidate(); if (this.app.updateSkyUi) this.app.updateSkyUi(); }
    /** BSP-Ansicht: Flächen mit der Sky-Textur weglassen (für Übersichten von oben) */
    /** BSP-Ansicht: Decken (nach unten zeigende Flächen) und Dächer (Oberseiten der Platten darüber) weglassen, um von oben in die Räume zu sehen */
    setHideCeilings(on) { this.hideCeil = !!on; if (this.bsp) { this.setBsp(this.bsp); this.dirty.geo = true; this.invalidate(); } }
    setHideSky(on) { this.hideSky = !!on; if (this.bsp) { this.setBsp(this.bsp); this.dirty.geo = true; this.invalidate(); } }
    setBspGamma(on) { this.bspGamma = !!on; if (this.bsp) { this.setBsp(this.bsp); this.dirty.geo = true; this.invalidate(); } }
    setBspLit(on) { this.bspLit = !!on; this.lit = !!(on && this.bsp && this.lmTex); this.dirty.geo = true; this.invalidate(); }
    /** Lightmap-Ergebnis setzen (oder null) und Lichtdarstellung ein- oder ausschalten. */
    setLight(result, on, exposure) {
      if (!this.ok) return;
      if (result !== this.lm) {
        if (this.lmTex) { this.lmTex.dispose(); this.lmTex = null; }
        this.lm = result;
        if (result) {
          const c = document.createElement('canvas'); c.width = result.W; c.height = result.H;
          c.getContext('2d').putImageData(new ImageData(result.data, result.W, result.H), 0, 0);
          const t = new THREE.CanvasTexture(c);
          t.flipY = false; t.channel = 1; t.generateMipmaps = false; t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
          this.lmTex = t;
        }
      }
      this.exposure = exposure === undefined ? this.exposure : exposure;
      this.lit = !!(on && this.lm && this.lmTex);
      this.dirty.geo = true; this.invalidate();
    }
    setExposure(x) {
      this.exposure = x;
      this.meshes.concat(this.bspMeshes).forEach((m) => { if (m.material.lightMap) m.material.lightMapIntensity = x; });
      this.invalidate();
    }
    texturesChanged() {
      this.texCache.forEach((t) => t && t.dispose && t.dispose());
      this.texCache = new Map(); this.mdlCache = new Map(); this.dirty.geo = true; this.invalidate();
    }

    getTexture(name) {
      const k = name.toUpperCase();
      if (this.texCache.has(k)) return this.texCache.get(k);
      let tex = null;
      try {
        const px = (this.bsp && this.bsp.textures.get(k)) || this.app.wads.pixels(name);
        if (px) {
          const c = document.createElement('canvas'); c.width = px.w; c.height = px.h;
          const ctx = c.getContext('2d'), id = ctx.createImageData(px.w, px.h);
          id.data.set(px.rgba); ctx.putImageData(id, 0, 0);
          tex = new THREE.CanvasTexture(c);
          tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
          tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.LinearMipmapLinearFilter;
          tex.anisotropy = 4; tex.userData = { w: px.w, h: px.h };
        }
      } catch (err) { tex = null; }
      this.texCache.set(k, tex);
      return tex;
    }

    /** Höhe (Karten-Koordinate Z) der ersten festen Fläche unter dem Punkt innerhalb von DROP_MAX Einheiten, sonst null. Das Ergebnis wird
     *  gemerkt, solange sich die Welt nicht ändert (Summe der Punkte der Welt-Netze als Kennung). */
    floorBelow(o) {
      const world = this.bsp ? this.bspMeshes : this.meshes;
      const sig = world.reduce((a, m) => a + m.geometry.attributes.position.count, 0) + '|' + world.length;
      if (!this.floorCache || this.floorCache.sig !== sig) this.floorCache = { sig, map: new Map() };
      const key = o[0] + ',' + o[1] + ',' + o[2];
      if (this.floorCache.map.has(key)) return this.floorCache.map.get(key);
      this.ray.set(new THREE.Vector3(o[0], o[2] + 1, -o[1]), new THREE.Vector3(0, -1, 0)); this.ray.far = DROP_MAX + 1;
      let z = null;
      for (const h of this.ray.intersectObjects(world, false)) {
        const mat = h.object.material;
        if (mat.transparent && mat.opacity < 0.5) continue;   // Wasser, Trigger, Glas: das Spiel fällt hindurch oder die Fläche zählt nicht als Boden
        z = h.point.y; break;
      }
      this.ray.far = Infinity;
      this.floorCache.map.set(key, z);
      return z;
    }

    /** Modell einer Entity als Objekt (Gruppe von Netzen je Textur), in Koordinaten der 3D-Ansicht; null, wenn das Modell nicht geladen ist.
     *  Geometrie und Texturen werden je Modell, body, skin und Sequenz geteilt. */
    modelObject(e, o) {
      const path = modelPathOf(e);
      if (!path || !this.app.showModels) return null;
      const num = (v) => { const n = parseInt(v, 10); return isFinite(n) ? n : 0; };
      const opts = { body: num(e.kv.body), skin: num(e.kv.skin), seq: num(e.kv.sequence), frame: parseFloat(e.kv.frame) || 0 };
      const groups = this.app.models.geometry(path, opts), mdl = this.app.models.get(path);
      if (!groups || !groups.length || !mdl) return null;
      if (!this.mdlCache) this.mdlCache = new Map();
      const id = path.toLowerCase() + '|' + opts.body + '|' + opts.skin + '|' + opts.seq + '|' + opts.frame;
      let parts = this.mdlCache.get(id);
      if (!parts) {
        // Modell waagerecht um die Mitte und mit der Unterkante auf 0 ausrichten; die Box der Entity setzt es später an die richtige Stelle
        const bb = geometryBounds(groups), anchor = modelAnchor(bb, boxOf(e.kv.classname));
        parts = groups.map((g) => {
          const n = g.pos.length / 3, pos = new Float32Array(n * 3), nrm = new Float32Array(n * 3), uv = new Float32Array(n * 2), col = new Float32Array(n * 3);
          const t = mdl.textures[g.texture], fl = t ? t.flags : 0, bright = (fl & 4) !== 0 || (fl & 0x20) !== 0;
          for (let i = 0; i < n; i++) {
            const x = g.pos[i * 3] + anchor.shift[0], y = g.pos[i * 3 + 1] + anchor.shift[1], z = g.pos[i * 3 + 2] + anchor.shift[2], nx = g.nrm[i * 3], ny = g.nrm[i * 3 + 1], nz = g.nrm[i * 3 + 2];
            pos[i * 3] = x; pos[i * 3 + 1] = z; pos[i * 3 + 2] = -y;
            nrm[i * 3] = nx; nrm[i * 3 + 1] = nz; nrm[i * 3 + 2] = -ny;
            uv[i * 2] = g.uv[i * 2]; uv[i * 2 + 1] = 1 - g.uv[i * 2 + 1];
            const sh = bright ? 1 : 0.55 + 0.45 * Math.max(0, nx * LIGHT[0] + ny * LIGHT[1] + nz * LIGHT[2]);
            col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = sh;
          }
          const geo = new THREE.BufferGeometry();
          geo.setAttribute('position', new THREE.BufferAttribute(pos, 3)); geo.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
          geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2)); geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
          let map = null;
          if (t && t.w && t.h) {
            const ck = 'MDL:' + path.toLowerCase() + ':' + g.texture;
            map = this.texCache.get(ck);
            if (!map) {
              const c = document.createElement('canvas'); c.width = t.w; c.height = t.h;
              const id2 = c.getContext('2d').createImageData(t.w, t.h); id2.data.set(t.rgba); c.getContext('2d').putImageData(id2, 0, 0);
              map = new THREE.CanvasTexture(c); map.wrapS = map.wrapT = THREE.RepeatWrapping; map.magFilter = THREE.NearestFilter; map.anisotropy = 4;
              this.texCache.set(ck, map);
            }
          }
          const o = { vertexColors: true, side: THREE.DoubleSide };
          if (map) o.map = map; else o.color = 0xb0b0b0;
          if (t && (t.flags & 0x40)) o.alphaTest = 0.5;                                             // maskiert
          if (t && (t.flags & 0x20)) { o.transparent = true; o.blending = THREE.AdditiveBlending; o.depthWrite = false; }   // additiv
          return { geo, o };
        });
        parts.bb = bb;
        this.mdlCache.set(id, parts);
      }
      const grp = new THREE.Group();
      const at = modelAnchor(parts.bb, boxOf(e.kv.classname)).at.slice();
      // Waffen, Munition und Gegenstände fallen im Spiel auf den Boden: das Modell steht dort, nicht auf der Höhe des Ursprungs
      if (dropsToFloor(e.kv.classname) && o) { const fz = this.floorBelow(o); if (fz !== null && fz <= o[2] + 1) at[2] = fz - o[2]; }
      grp.position.set(at[0], at[2], -at[1]);   // Mitte und Unterseite der Box (relativ zum Ursprung der Entity)
      parts.forEach((p) => { const mesh = new THREE.Mesh(p.geo, new THREE.MeshBasicMaterial(p.o)); if (p.o.blending) mesh.renderOrder = 3; grp.add(mesh); });
      // Blickrichtung: angles "pitch yaw roll" (oder angle) – die Drehung um Z (yaw) entspricht der Drehung um die Hochachse der Ansicht
      const a = String(e.kv.angles || '').trim().split(/\s+/).map(Number), yaw = isFinite(a[1]) && a.length >= 2 ? a[1] : (parseFloat(e.kv.angle) || 0);
      grp.rotation.y = (yaw * Math.PI) / 180;
      return grp;
    }

    clearGroup(g) {
      const kill = (c) => {
        c.children.forEach(kill);
        if (c.geometry) c.geometry.dispose();
        if (c.material && c.material.dispose) c.material.dispose();
      };
      while (g.children.length) kill(g.children.pop());
    }

    /** Himmelsfarbe (skyname) und Nebel (env_fog) nur in der Lichtvorschau. */
    applyEnv(doc) {
      const C = this.app.colors();
      let bg = new THREE.Color(C.bg), fog = null;
      this.skyRGB = null;
      if (this.lit) {
        const w = doc.ents[0], sky = rgbOf(SKY_COL[String(w.kv.skyname || '').toLowerCase()] || SKY_COL.default);
        this.skyRGB = sky; bg = new THREE.Color(cssRgb(sky));
        const fe = doc.ents.find((e) => e.kv.classname === 'env_fog');
        if (fe) {
          const c = String(fe.kv.rendercolor || '128 128 128').trim().split(/\s+/).map(Number);
          const col = [(c[0] || 0) / 255, (c[1] || 0) / 255, (c[2] || 0) / 255];
          const st = Number(fe.kv.startdist), en = Number(fe.kv.enddist);
          fog = new THREE.Fog(new THREE.Color(cssRgb(col)), isNaN(st) ? 500 : st, isNaN(en) || en <= st ? (isNaN(st) ? 1500 : st + 1000) : en);
          bg = new THREE.Color(cssRgb(col));
        }
      }
      this.skyOn = skyVisible(this.app.skybox, doc.ents[0].kv.skyname);   // nur die Skybox der Karte oder eine gewählte Vorschau
      if (this.skyMesh) { this.scene.remove(this.skyMesh); this.skyMesh = null; }
      if (this.skyOn) {
        // Würfel um die Kamera, von innen gesehen; Reihenfolge der Seiten: +x,-x,+y,-y,+z,-z (three.js-Koordinaten)
        const m = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), this.app.skybox.mats);
        m.frustumCulled = false; m.renderOrder = -10; this.skyMesh = m; this.scene.add(m);
      }
      this.scene.background = bg; this.scene.fog = fog;
      if (this.grid) this.grid.visible = !this.lit;
    }
    /** Darstellung von Brush-Entities mit rendermode/renderamt und Wasser. */
    renderProps(e, ei) {
      if (ei === 0) return null;
      const rm = Number(e.kv.rendermode) || 0, amt = e.kv.renderamt !== undefined && e.kv.renderamt !== '' ? Math.max(0, Math.min(255, Number(e.kv.renderamt))) / 255 : 1;
      if (rm === 0 && e.kv.classname === 'func_water') return { op: 0.6, tint: [0.55, 0.75, 1], add: false };
      if (rm === 5) return { op: amt, tint: null, add: true };
      if ((rm === 1 || rm === 2 || rm === 3) && amt < 1) return { op: amt, tint: null, add: false };
      return null;
    }
    snapshot() {
      if (!this.ok) return null;
      this.frame(performance.now());
      this.updateSky(); this.renderer.render(this.scene, this.camera);
      return this.canvas.toDataURL('image/png');
    }
    setView(o) {
      if (o.player !== undefined) this.showPlayer = !!o.player;
      if (o.lmOnly !== undefined) this.lmOnly = !!o.lmOnly;
      this.dirty.geo = true; this.invalidate();
    }

    buildGeo() {
      this.clearGroup(this.world); this.clearGroup(this.ents);
      this.meshes = []; this.entMeshes = []; this.bspMeshes = [];
      const doc = this.doc; if (!doc) return;
      if (this.bsp) this.buildBspGeo(doc);
      const groups = new Map();
      let nb = 0;
      this.applyEnv(doc);
      const plain = this.lit && this.lmOnly;
      // Flächendaten je Brush merken (Brush-Objekte sind unveränderlich): beim Formen ändern sich nur wenige Brushes, der Rest kommt aus dem Merker.
      // Der Merker gilt nur, solange alles gleich bleibt, was in die Flächendaten eingeht (Texturen, Lightmap, Himmelsfarbe, Schalter).
      const gk = this.fcKey, sky = this.skyRGB ? this.skyRGB.join() : '';
      if (!gk || gk.tex !== this.texCache || gk.lm !== this.lm || gk.lit !== this.lit || gk.plain !== plain || gk.ents !== this.app.showEnts || gk.sky !== sky) {
        this.fc = new WeakMap(); this.fcKey = { tex: this.texCache, lm: this.lm, lit: this.lit, plain: plain, ents: this.app.showEnts, sky: sky };
      }
      const faceData = (b, e, ei, rp, cls, trig) => {
        const out = [];
        b.faces.forEach((f, fi) => {
          if (!this.app.showEnts && (ei > 0 ? isFunctionBrush(cls, f.tex) : isFunctionBrush('', f.tex))) return;   // Funktions-Brushes hängen am Entities-Schalter
          const n = Core.faceNormal(b.verts, f.vi), tn = f.tex.toUpperCase();
          const special = SPECIAL_COL[tn] ? tn : (trig ? 'AAATRIGGER' : null);
          const tex = special || plain ? null : this.getTexture(f.tex);
          const key = (special ? '#' + special : plain ? 'C:LM' : (tex ? 'T:' + tn : 'C:' + tn)) + (rp ? '|' + rp.op.toFixed(2) + (rp.add ? 'a' : '') + (rp.tint ? 't' : '') : '');
          const lmi = this.lit && !special ? (this.lm.map.get(b) || [])[fi] : null;
          const sh = this.lit ? 1 : 0.5 + 0.5 * Math.max(0, Core.dot(n, LIGHT)) * 0.9 + (n[2] < -0.5 ? -0.15 : 0);
          let base = special ? (special === 'SKY' && this.skyRGB ? this.skyRGB : SPECIAL_COL[special]) : (tex || plain ? [1, 1, 1] : hashCol(tn));
          if (rp && rp.tint) base = [base[0] * rp.tint[0], base[1] * rp.tint[1], base[2] * rp.tint[2]];
          const w = tex ? tex.userData.w : 64, h = tex ? tex.userData.h : 64;
          const xs = f.xs || 1, ys = f.ys || 1;
          const vv = f.vi.map((i) => {
            const p = b.verts[i];
            return { p: p, u: (Core.dot(p, f.ua) / xs + f.uo) / w, v: -(Core.dot(p, f.va) / ys + f.vo) / h, l: this.lit ? (lmi ? Light.uvFor(lmi, p, this.lm.W, this.lm.H) : Light.whiteUV(this.lm.W, this.lm.H)) : null };
          });
          const r = { key: key, tex: tex, special: special, name: tn, pos: [], uv: [], uv2: [], col: [], map: [] };
          for (let k = 1; k < vv.length - 1; k++) {
            [vv[0], vv[k], vv[k + 1]].forEach((q) => {
              r.pos.push(q.p[0], q.p[2], -q.p[1]); r.uv.push(q.u, q.v); if (q.l) r.uv2.push(q.l[0], q.l[1]);
              r.col.push(base[0] * sh, base[1] * sh, base[2] * sh);
            });
            r.map.push(b.id, fi);
          }
          out.push(r);
        });
        return out;
      };
      const push = (dst, src) => { for (let i = 0; i < src.length; i++) dst.push(src[i]); };
      doc.ents.forEach((e, ei) => {
        const rp = this.renderProps(e, ei);
        const cls = e.kv.classname || '';
        const trig = ei > 0 && /^trigger_/.test(cls);
        const sig = (ei > 0 ? 1 : 0) + '|' + cls + '|' + (trig ? 1 : 0) + '|' + (rp ? rp.op + (rp.add ? 'a' : '') + (rp.tint ? rp.tint.join() : '') : '');
        e.brushes.forEach((b) => {
          nb++;
          let c = this.fc.get(b);
          if (!c || c.sig !== sig) { c = { sig: sig, faces: faceData(b, e, ei, rp, cls, trig) }; this.fc.set(b, c); }
          c.faces.forEach((r) => {
            let g = groups.get(r.key);
            if (!g) { g = { key: r.key, tex: r.tex, special: r.special, rp: rp, name: r.name, pos: [], uv: [], uv2: [], col: [], map: [] }; groups.set(r.key, g); }
            push(g.pos, r.pos); push(g.uv, r.uv); push(g.uv2, r.uv2); push(g.col, r.col); push(g.map, r.map);
          });
        });
      });
      groups.forEach((g) => {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.Float32BufferAttribute(g.pos, 3));
        geo.setAttribute('uv', new THREE.Float32BufferAttribute(g.uv, 2));
        geo.setAttribute('color', new THREE.Float32BufferAttribute(g.col, 3));
        if (this.lit) geo.setAttribute('uv1', new THREE.Float32BufferAttribute(g.uv2, 2));
        const o = { vertexColors: true, side: THREE.FrontSide };
        if (this.lit) { o.lightMap = this.lmTex; o.lightMapIntensity = this.exposure; }
        if (g.tex) { o.map = g.tex; if (g.name.charAt(0) === '{') o.alphaTest = 0.5; }
        if (g.special && SPECIAL_COL[g.special][3] < 1) { o.transparent = true; o.opacity = SPECIAL_COL[g.special][3]; o.depthWrite = false; }
        if (g.special === 'SKY' && this.skyOn) o.colorWrite = false;
        if (g.rp) { o.transparent = true; o.opacity = g.rp.op; o.depthWrite = false; if (g.rp.add) o.blending = THREE.AdditiveBlending; }
        const m = new THREE.Mesh(geo, new THREE.MeshBasicMaterial(o));
        if (g.rp) m.renderOrder = 1;
        m.userData.tris = g.map;
        this.world.add(m); this.meshes.push(m);
      });
      // Punkt-Entities
      doc.ents.forEach((e, i) => {
        if (!this.app.showEnts || !Cmd.isPointEnt(e, i)) return;   // ausgeblendete Entities fehlen auch beim Anklicken
        const o = Core.parseVec(e.kv.origin); if (!o) return;
        const col = new THREE.Color(EntDefs.colorFor(e.kv.classname));
        // Box der Klasse (size() aus der FGD), sonst ein Würfel; die Mitte der Box liegt relativ zum Ursprung der Entity
        const bb = boxOf(e.kv.classname);
        const geo = new THREE.BoxGeometry(bb ? Math.max(bb.size[0], 2) : 16, bb ? Math.max(bb.size[2], 2) : 16, bb ? Math.max(bb.size[1], 2) : 16);
        if (bb) geo.translate(bb.center[0], bb.center[2], -bb.center[1]);
        const m = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: col }));
        m.position.copy(T(o)); m.userData.entId = e.id;
        const ed = new THREE.LineSegments(new THREE.EdgesGeometry(m.geometry), new THREE.LineBasicMaterial({ color: 0x000000 }));
        m.add(ed);
        if (/^(info_player|monster_)/.test(e.kv.classname) && e.kv.angles) {
          const a = e.kv.angles.trim().split(/\s+/).map(Number), r = (a[1] || 0) * Math.PI / 180;
          m.add(new THREE.ArrowHelper(new THREE.Vector3(Math.cos(r), 0, -Math.sin(r)), new THREE.Vector3(), 40, col.getHex(), 12, 8));
        }
        const ik = EntDefs.iconKind(e.kv.classname);
        if (ik) { // Symbol-Billboard (Lautsprecher) statt Würfel; immer sichtbar, auch durch Wände
          if (!this.iconTex) this.iconTex = {};
          if (!this.iconTex[ik + col.getHexString()]) {
            const c = document.createElement('canvas'); c.width = c.height = 128; const cx = c.getContext('2d');
            EntDefs.drawIcon(cx, ik, 64, 64, 104, '#' + col.getHexString(), 'rgba(15,18,24,0.85)');
            this.iconTex[ik + col.getHexString()] = new THREE.CanvasTexture(c);
          }
          const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.iconTex[ik + col.getHexString()], transparent: true, depthTest: false, depthWrite: false }));
          sp.scale.set(40, 40, 1); sp.renderOrder = 5; m.add(sp);
          m.material.transparent = true; m.material.opacity = 0; m.material.depthWrite = false; ed.visible = false;
        }
        const spr = this.app.spriteFor(e);
        if (spr) {
          // Sprite als Billboard: Größe = Bildgröße × scale, Farbe/Deckkraft aus rendercolor/renderamt, additiv bei rendermode 5 oder additivem Format
          if (!spr.tex) spr.tex = new THREE.CanvasTexture(spr.canvas);
          const sc = parseFloat(e.kv.scale); const k = isFinite(sc) && sc > 0 ? sc : 1, rm = parseInt(e.kv.rendermode, 10) || 0;
          const rc = (e.kv.rendercolor || '255 255 255').trim().split(/\s+/).map((x) => (parseFloat(x) || 0) / 255), ra = e.kv.renderamt !== undefined && e.kv.renderamt !== '' ? Math.max(0, Math.min(255, parseFloat(e.kv.renderamt))) / 255 : 1;
          const add = rm === 5 || spr.format === 1, mat = new THREE.SpriteMaterial({ map: spr.tex, color: new THREE.Color(rc[0], rc[1], rc[2]), transparent: true, opacity: add || rm ? ra : 1, depthWrite: false, blending: add ? THREE.AdditiveBlending : THREE.NormalBlending });
          const sp = new THREE.Sprite(mat); sp.scale.set(spr.w * k, spr.h * k, 1); sp.renderOrder = 3; m.add(sp);
          m.material.transparent = true; m.material.opacity = 0.25; m.material.depthWrite = false;
        }
        const mo = this.modelObject(e, o);
        if (mo) {   // Modell statt Box: die Box bleibt unsichtbar als Trefferfläche zum Anklicken
          m.add(mo);
          m.material.transparent = true; m.material.opacity = 0; m.material.depthWrite = false; ed.visible = false;
        }
        this.ents.add(m); this.entMeshes.push(m);
        if (this.showPlayer && /^info_player_(start|deathmatch)$/.test(e.kv.classname)) {
          // Spielerhülle im Stehen: 32 × 32 × 72 Einheiten um den Ursprung, dazu die Augenhöhe bei +28
          const hull = new THREE.Mesh(new THREE.BoxGeometry(32, 72, 32), new THREE.MeshBasicMaterial({ color: 0x46c98b, transparent: true, opacity: 0.3, depthWrite: false }));
          hull.position.copy(T(o)); hull.renderOrder = 2;
          hull.add(new THREE.LineSegments(new THREE.EdgesGeometry(hull.geometry), new THREE.LineBasicMaterial({ color: 0x1f8a5a })));
          const eye = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(32, 0.5, 32)), new THREE.LineBasicMaterial({ color: 0xffffff }));
          eye.position.y = 28; hull.add(eye);
          this.ents.add(hull);
        }
      });
      this.stats = { brushes: nb };
    }

    /** Welt aus einer BSP: ein Mesh je Textur, Lightmaps aus dem BSP-Atlas. */
    buildBspGeo(doc) {
      this.applyEnv(doc);
      const lit = this.lit && this.lmTex, A = this.bsp.atlas, groups = new Map();
      // Brush-Entities (func_wall, func_illusionary, func_door …) zeigen per "model" "*N" auf ein Submodell; ihre Render-Eigenschaften gelten für dessen Flächen
      const modelRp = new Map(), modelCls = new Map();
      (doc.ents || []).forEach((e, ei) => {
        const m = /^\*(\d+)$/.exec(String(e.kv.model || '')); if (!m) return;
        modelCls.set(Number(m[1]), e.kv.classname || '');
        const rp = this.renderProps(e, ei); if (rp) modelRp.set(Number(m[1]), rp);
      });
      // Maske der Decken und Dächer einmal je BSP berechnen und an der Szene merken
      if (this.hideCeil && !this.bsp.ceilMask) this.bsp.ceilMask = ceilingMask(this.bsp.polys);
      const cm = this.hideCeil ? this.bsp.ceilMask : null;
      this.bsp.polys.forEach((p, pi) => {
        if (this.hideSky && isSkyFace(p.tex)) return;
        if (cm && cm[pi]) return;
        if (!this.app.showEnts && isFunctionBrush(p.model ? modelCls.get(p.model) || '' : '', p.tex)) return;   // Funktions-Brushes hängen am Entities-Schalter
        const rp = p.model ? modelRp.get(p.model) || null : null;
        const tn = p.tex.toUpperCase(), c0 = tn.charAt(0);
        const special = SPECIAL_COL[tn] ? tn : null, water = c0 === '!' || c0 === '*';
        const tex = special ? null : this.getTexture(p.tex);
        const key = (special ? '#' + special : (tex ? 'T:' + tn : 'C:' + tn)) + (rp ? '|' + rp.op + (rp.add ? 'a' : '') : '');
        let g = groups.get(key);
        if (!g) { g = { tex: tex, special: special, name: tn, water: water, rp: rp, pos: [], uv: [], uv2: [], col: [] }; groups.set(key, g); }
        const v = p.verts, n = Core.norm(Core.cross(Core.sub(v[1], v[0]), Core.sub(v[2], v[0])));
        const sh = lit ? 1 : 0.5 + 0.5 * Math.max(0, Core.dot(Core.mul(n, -1), LIGHT)) * 0.9;
        let base = special ? (special === 'SKY' && this.skyRGB ? this.skyRGB : SPECIAL_COL[special]) : (tex ? [1, 1, 1] : hashCol(tn));
        const w = tex ? tex.userData.w : p.w, h = tex ? tex.userData.h : p.h;
        for (let k = 1; k < v.length - 1; k++) {
          [0, k, k + 1].forEach((i) => {
            g.pos.push(v[i][0], v[i][2], -v[i][1]); g.uv.push(p.S[i] / w, -p.T[i] / h);
            if (lit) { const l = p.lm ? p.lm[i] : [2 / A.W, 2 / A.H]; g.uv2.push(l[0], l[1]); }
            g.col.push(base[0] * sh, base[1] * sh, base[2] * sh);
          });
        }
      });
      groups.forEach((g) => {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.Float32BufferAttribute(g.pos, 3));
        geo.setAttribute('uv', new THREE.Float32BufferAttribute(g.uv, 2));
        geo.setAttribute('color', new THREE.Float32BufferAttribute(g.col, 3));
        if (lit) geo.setAttribute('uv1', new THREE.Float32BufferAttribute(g.uv2, 2));
        // Die BSP-Dreiecke laufen in Spielrichtung gegen den Uhrzeigersinn von vorn gesehen: wie bei den Brushes FrontSide
        const o = { vertexColors: true, side: THREE.DoubleSide };
        if (lit) { o.lightMap = this.lmTex; o.lightMapIntensity = this.exposure; }
        if (g.tex) { o.map = g.tex; if (g.name.charAt(0) === '{') o.alphaTest = 0.5; }
        if (g.special && SPECIAL_COL[g.special][3] < 1) { o.transparent = true; o.opacity = SPECIAL_COL[g.special][3]; o.depthWrite = false; }
        if (g.special === 'SKY' && this.skyOn) o.colorWrite = false;
        if (g.water) { o.transparent = true; o.opacity = 0.6; o.depthWrite = false; }
        if (g.rp) { o.transparent = true; o.opacity = g.rp.op; o.depthWrite = false; if (g.rp.add) o.blending = THREE.AdditiveBlending; }
        const m = new THREE.Mesh(geo, new THREE.MeshBasicMaterial(o));
        if (g.water) m.renderOrder = 1;
        if (g.rp) m.renderOrder = 2;
        this.world.add(m); this.bspMeshes.push(m);
      });
    }

    buildSel() {
      this.clearGroup(this.overlay);
      const app = this.app, doc = this.doc; if (!doc) return;
      const sel = app.sel, bs = Cmd.selBrushes(doc, sel);
      if (bs.length) {
        const lines = [], fill = [], face = [];
        const fkeys = app.faceKey();
        bs.forEach((b) => {
          b.faces.forEach((f, fi) => {
            f.vi.forEach((a, k) => {
              const c = f.vi[(k + 1) % f.vi.length];
              if (a < c) lines.push(b.verts[a][0], b.verts[a][2], -b.verts[a][1], b.verts[c][0], b.verts[c][2], -b.verts[c][1]);
            });
            const isF = fkeys.size > 0 && fkeys.has(b.id + ':' + fi);
            for (let k = 1; k < f.vi.length - 1; k++) {
              [f.vi[0], f.vi[k], f.vi[k + 1]].forEach((i) => (isF ? face : fill).push(b.verts[i][0], b.verts[i][2], -b.verts[i][1]));
            }
          });
        });
        const C = new THREE.Color(app.colors().sel);
        const mk = (arr, opacity) => {
          if (!arr.length) return;
          const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(arr, 3));
          const m = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color: C, transparent: true, opacity: opacity, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 }));
          this.overlay.add(m);
        };
        mk(fill, 0.22); mk(face, 0.6);
        const lg = new THREE.BufferGeometry(); lg.setAttribute('position', new THREE.Float32BufferAttribute(lines, 3));
        this.overlay.add(new THREE.LineSegments(lg, new THREE.LineBasicMaterial({ color: C, depthTest: false, transparent: true })));
      }
      const es = Cmd.selEnts(doc, sel);
      const lp = [];
      const seg = (a, b) => lp.push(a[0], a[2], -a[1], b[0], b[2], -b[1]);
      const circle = (c, u, v, r) => {
        for (let i = 0; i < 32; i++) {
          const a0 = i / 32 * 6.2832, a1 = (i + 1) / 32 * 6.2832;
          seg([c[0] + (u[0] * Math.cos(a0) + v[0] * Math.sin(a0)) * r, c[1] + (u[1] * Math.cos(a0) + v[1] * Math.sin(a0)) * r, c[2] + (u[2] * Math.cos(a0) + v[2] * Math.sin(a0)) * r],
            [c[0] + (u[0] * Math.cos(a1) + v[0] * Math.sin(a1)) * r, c[1] + (u[1] * Math.cos(a1) + v[1] * Math.sin(a1)) * r, c[2] + (u[2] * Math.cos(a1) + v[2] * Math.sin(a1)) * r]);
        }
      };
      es.forEach((e) => {
        if (!/^light(_spot|_environment)?$/.test(e.kv.classname || '')) return;
        const L = Light.collect(doc, app.light && app.light.flip, e)[0]; if (!L) return;
        if (L.type === 'point') {
          const R = Light.range(L.i);
          circle(L.p, [1, 0, 0], [0, 1, 0], R); circle(L.p, [1, 0, 0], [0, 0, 1], R); circle(L.p, [0, 1, 0], [0, 0, 1], R);
        } else if (L.type === 'spot') {
          const R = Light.range(L.i), d = L.dir, h = Math.abs(d[2]) > 0.9 ? [1, 0, 0] : [0, 0, 1], u = Core.norm(Core.cross(d, h)), v = Core.cross(d, u);
          seg(L.p, Core.add(L.p, Core.mul(d, R)));
          [Math.acos(L.cin), Math.acos(L.cout)].forEach((a, k) => {
            const c = Core.add(L.p, Core.mul(d, R * Math.cos(a))), r = R * Math.sin(a);
            circle(c, u, v, r);
            if (k) [[1, 0], [0, 1], [-1, 0], [0, -1]].forEach((q) => seg(L.p, Core.add(c, Core.add(Core.mul(u, q[0] * r), Core.mul(v, q[1] * r)))));
          });
        } else {
          const o = Core.parseVec(e.kv.origin) || [0, 0, 0], d = L.dir, tip = Core.add(o, Core.mul(d, 320));
          seg(o, tip);
          const h = Math.abs(d[2]) > 0.9 ? [1, 0, 0] : [0, 0, 1], u = Core.norm(Core.cross(d, h));
          seg(tip, Core.add(Core.sub(tip, Core.mul(d, 40)), Core.mul(u, 18))); seg(tip, Core.sub(Core.sub(tip, Core.mul(d, 40)), Core.mul(u, 18)));
        }
      });
      // Türen (offener Zustand, Weg, Drehpunkt) und target-Verbindungen
      const dp = [], kp = [], dseg = (a, b) => dp.push(a[0], a[2], -a[1], b[0], b[2], -b[1]), all = app.showAids;
      const meanP = (pts) => [0, 1, 2].map((i) => pts.reduce((s, q) => s + q[i], 0) / pts.length);
      doc.ents.forEach((e) => {
        if (!Doors.active(e, sel, all)) return;
        const d = Doors.describe(e); if (!d) return;
        Doors.EDGES.forEach((ed) => dseg(d.c1[ed[0]], d.c1[ed[1]]));
        const m0 = meanP(d.c0);
        if (d.kind === 'slide') dseg(m0, meanP(d.c1));
        else {
          const pv = d.pivot, M = Core.rotMat(d.axis, d.deg);
          [[8, 0, 0], [0, 8, 0], [0, 0, 8]].forEach((q) => dseg(Core.sub(pv, q), Core.add(pv, q)));
          let prev = m0;
          for (let k = 1; k <= 16; k++) {
            const Mk = Core.rotMat(d.axis, d.deg * k / 16), q = Core.sub(m0, pv), pk = Core.add([Core.dot(Mk[0], q), Core.dot(Mk[1], q), Core.dot(Mk[2], q)], pv);
            dseg(prev, pk); prev = pk;
          }
        }
      });
      Doors.links(doc).forEach((l) => {
        if (!all && !Doors.active(l.from, sel, false) && !Doors.active(l.to, sel, false)) return;
        kp.push(l.a[0], l.a[2], -l.a[1], l.b[0], l.b[2], -l.b[1]);
      });
      [[dp, 0xff9f1a], [kp, 0x6fa8ff]].forEach((x) => {
        if (!x[0].length) return;
        const g3 = new THREE.BufferGeometry(); g3.setAttribute('position', new THREE.Float32BufferAttribute(x[0], 3));
        this.overlay.add(new THREE.LineSegments(g3, new THREE.LineBasicMaterial({ color: x[1], transparent: true, opacity: 0.8, depthTest: false })));
      });
      if (lp.length) {
        const lg2 = new THREE.BufferGeometry(); lg2.setAttribute('position', new THREE.Float32BufferAttribute(lp, 3));
        this.overlay.add(new THREE.LineSegments(lg2, new THREE.LineBasicMaterial({ color: 0xffd24a, transparent: true, opacity: 0.55, depthTest: false })));
      }
      if (app.leak) {
        const lk = [], L = app.leak.pts;
        for (let i = 1; i < L.length; i++) lk.push(L[i - 1][0], L[i - 1][2], -L[i - 1][1], L[i][0], L[i][2], -L[i][1]);
        [L[0], L[L.length - 1]].forEach((p) => { [[12, 0, 0], [0, 12, 0], [0, 0, 12]].forEach((q) => lk.push(p[0] - q[0], p[2] - q[2], -(p[1] - q[1]), p[0] + q[0], p[2] + q[2], -(p[1] + q[1]))); });
        const lg3 = new THREE.BufferGeometry(); lg3.setAttribute('position', new THREE.Float32BufferAttribute(lk, 3));
        this.overlay.add(new THREE.LineSegments(lg3, new THREE.LineBasicMaterial({ color: 0xff2d4d, depthTest: false })));
      }
      if (app.marks && app.marks.length) {
        // Überlagerte Flächen o. Ä.: rote Füllung und Rand, leicht entlang der Flächennormale angehoben und ohne Tiefentest
        const fill = [], edge = [];
        app.marks.forEach((poly) => {
          if (poly.length < 3) return;
          const q = poly.map((p) => [p[0], p[2], -p[1]]);
          for (let i = 1; i < q.length - 1; i++) fill.push(...q[0], ...q[i], ...q[i + 1]);
          for (let i = 0; i < q.length; i++) edge.push(...q[i], ...q[(i + 1) % q.length]);
        });
        const mg = new THREE.BufferGeometry(); mg.setAttribute('position', new THREE.Float32BufferAttribute(fill, 3));
        this.overlay.add(new THREE.Mesh(mg, new THREE.MeshBasicMaterial({ color: 0xff2d4d, transparent: true, opacity: 0.45, side: THREE.DoubleSide, depthTest: false })));
        const me = new THREE.BufferGeometry(); me.setAttribute('position', new THREE.Float32BufferAttribute(edge, 3));
        this.overlay.add(new THREE.LineSegments(me, new THREE.LineBasicMaterial({ color: 0xff2d4d, depthTest: false })));
      }
      es.forEach((e) => {
        const o = Core.parseVec(e.kv.origin); if (!o) return;
        const w = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(24, 24, 24)), new THREE.LineBasicMaterial({ color: new THREE.Color(app.colors().sel), depthTest: false }));
        w.position.copy(T(o)); this.overlay.add(w);
      });
    }

    // ------------------------------------------------------------ Zeichnen
    invalidate() {
      if (this.pending || !this.ok) return;
      this.pending = true;
      requestAnimationFrame((t) => { this.pending = false; this.frame(t); });
    }
    /** Volle Auflösung im Stillstand; während Kamerabewegung und Strichen 1, das spart auf Retina bis zu drei Viertel der Pixel */
    pixelRatio() { const full = Math.min(window.devicePixelRatio || 1, 2); return this.lowRes ? 1 : full; }
    /** Kurz auf niedrige Auflösung schalten; nach 200 ms Ruhe (und ohne laufenden Strich) kommt die volle zurück */
    busy() {
      if (!this.ok || (window.devicePixelRatio || 1) <= 1) return;
      clearTimeout(this.lowT);
      this.lowT = setTimeout(() => { if (this.stroke) { this.busy(); return; } this.lowRes = false; this.resize(); }, 200);
      if (!this.lowRes) { this.lowRes = true; this.resize(); }
    }
    resize() {
      if (!this.ok) return;
      const w = Math.max(10, this.host.clientWidth), h = Math.max(10, this.host.clientHeight);
      this.renderer.setPixelRatio(this.pixelRatio());
      this.renderer.setSize(w, h, false);
      this.camera.aspect = w / h; this.camera.updateProjectionMatrix();
      this.invalidate();
    }
    frame(t) {
      if (!this.ok) return;
      try {
        if (this.dirty.geo) { this.dirty.geo = false; this.buildGeo(); }
        if (this.dirty.sel) { this.dirty.sel = false; this.buildSel(); }
        const dt = Math.min(0.1, (t - (this.last || t)) / 1000); this.last = t;
        if (this.fly && this.keys.size) { this.busy(); this.stepFly(dt); }
        this.updateSky(); this.renderer.render(this.scene, this.camera);
      } catch (err) {
        if (!this.errShown) { this.errShown = true; this.app.status(_('v3.error', { msg: err.message })); }
      }
      if (this.fly && this.keys.size) this.invalidate(); else this.last = 0;
    }

    // ------------------------------------------------------------ Kamera
    /** dist: Abstand als Vielfaches des Hüllradius (größer = mehr Umgebung sichtbar) */
    fit(box, dist) {
      if (!this.ok) return;
      let mn, mx;
      if (box) { mn = T(box.min); mx = T(box.max); } else if (this.doc && Core.allBrushes(this.doc).length) {
        const bb = Core.bboxOf(Core.allBrushes(this.doc).map((x) => x.b)); mn = T(bb.min); mx = T(bb.max);
      } else { mn = new THREE.Vector3(-256, 0, -256); mx = new THREE.Vector3(256, 256, 256); }
      const lo = new THREE.Vector3(Math.min(mn.x, mx.x), Math.min(mn.y, mx.y), Math.min(mn.z, mx.z));
      const hi = new THREE.Vector3(Math.max(mn.x, mx.x), Math.max(mn.y, mx.y), Math.max(mn.z, mx.z));
      const c = lo.clone().add(hi).multiplyScalar(0.5), r = Math.max(hi.clone().sub(lo).length() / 2, 48);
      const d = r / Math.sin(this.camera.fov * Math.PI / 360) * (dist || 0.9);
      this.setFly(false);
      this.camera.position.copy(c).add(new THREE.Vector3(-0.7, 0.8, 1.0).normalize().multiplyScalar(d));
      if (this.controls) { this.controls.target.copy(c); this.controls.update(); } else this.camera.lookAt(c);
      this.camera.far = Math.max(60000, d * 8); this.camera.updateProjectionMatrix();
      this.invalidate();
    }
    setFly(on) {
      if (!this.ok || on === this.fly) return;
      this.fly = on; this.keys.clear();
      if (on) {
        const d = new THREE.Vector3(); this.camera.getWorldDirection(d);
        this.yaw = Math.atan2(d.x, -d.z); this.pitch = Math.asin(Math.max(-1, Math.min(1, d.y)));
        if (this.controls) this.controls.enabled = false;
      } else if (this.controls) {
        const d = new THREE.Vector3(); this.camera.getWorldDirection(d);
        this.controls.target.copy(this.camera.position).add(d.multiplyScalar(300));
        this.controls.enabled = true; this.controls.update();
      }
      this.host.classList.toggle('flying', on);
      this.invalidate();
    }
    applyLook() {
      const cp = Math.cos(this.pitch);
      const d = new THREE.Vector3(Math.sin(this.yaw) * cp, Math.sin(this.pitch), -Math.cos(this.yaw) * cp);
      this.camera.lookAt(this.camera.position.clone().add(d));
    }
    stepFly(dt) {
      const k = this.keys, sp = this.flySpeed * (k.has('shift') ? 3 : 1) * dt;
      const fwd = new THREE.Vector3(Math.sin(this.yaw) * Math.cos(this.pitch), Math.sin(this.pitch), -Math.cos(this.yaw) * Math.cos(this.pitch));
      const right = new THREE.Vector3(Math.cos(this.yaw), 0, Math.sin(this.yaw));
      const mv = new THREE.Vector3();
      if (k.has('w')) mv.add(fwd); if (k.has('s')) mv.sub(fwd);
      if (k.has('d')) mv.add(right); if (k.has('a')) mv.sub(right);
      if (k.has('e')) mv.y += 1; if (k.has('q')) mv.y -= 1;
      if (mv.lengthSq()) this.camera.position.add(mv.normalize().multiplyScalar(sp));
    }
    /** Werkzeug „Formen“: linke Maustaste gehört dem Pinsel; Kamera drehen mit Alt + Ziehen (Trackpad) oder rechter Maustaste */
    setSculptMode(on) {
      this.sculptOn = on;
      if (!on) { this.hoverEv = null; this.hover = null; this.showRing(null); this.setPreview(null); }
      if (!this.controls) return;
      this.controls.mouseButtons.LEFT = on ? null : THREE.MOUSE.ROTATE;
      this.controls.mouseButtons.RIGHT = on ? THREE.MOUSE.ROTATE : THREE.MOUSE.PAN;
      if (!this.sculptGate) {
        // vor OrbitControls (Capture): ohne Alt formt die linke Taste, die Kamera bleibt gesperrt; mit Alt dreht sie
        this.sculptGate = (e) => {
          if (!this.sculptOn || this.fly || e.button !== 0) return;
          this.controls.mouseButtons.LEFT = e.altKey ? THREE.MOUSE.ROTATE : null;
          this.controls.enabled = !!e.altKey;
        };
        this.sculptRelease = () => { if (!this.fly && this.controls) this.controls.enabled = true; };
        this.host.addEventListener('pointerdown', this.sculptGate, true);
        window.addEventListener('pointerup', this.sculptRelease, true);
        window.addEventListener('pointercancel', this.sculptRelease, true);
      }
    }
    /** Pinselkreis auf dem Gelände: äußerer Ring = Radius, innerer = halber Radius; pos null blendet aus */
    showRing(pos, dir = 0) {
      if (!this.ringGroup) {
        if (!pos) return;
        this.ringGroup = new THREE.Group(); this.ringGroup.renderOrder = 999;
        this.rayDown = new THREE.Raycaster();
        const mk = (color) => { const l = new THREE.LineLoop(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color, depthTest: false, transparent: true })); l.renderOrder = 999; l.frustumCulled = false; return l; };
        this.ringOuter = mk(0xffd24a); this.ringInner = mk(0xffd24a); this.ringInner.material.opacity = 0.45;
        this.ringGroup.add(this.ringOuter, this.ringInner);
        this.scene.add(this.ringGroup);
      }
      this.ringGroup.visible = !!pos;
      if (pos) {
        const r = this.app.sculptOpts.radius, N = 64;
        // Kreis in der Ebene quer zur Richtung, auf die Oberfläche projiziert (Karten-Koordinaten → three: x, z, −y)
        const D = [[0, 0, 1], [0, 0, -1], [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0]][dir];
        const E1 = dir < 2 ? [1, 0, 0] : [0, 0, 1], E2 = dir < 2 ? [0, 1, 0] : (dir < 4 ? [0, 1, 0] : [1, 0, 0]);
        const T = (q) => new THREE.Vector3(q[0], q[2], -q[1]);
        const build = (rad) => {
          const pts = [];
          for (let i = 0; i < N; i++) {
            const a = (i / N) * Math.PI * 2, c = Math.cos(a) * rad, s = Math.sin(a) * rad;
            const q = [pos[0] + E1[0] * c + E2[0] * s, pos[1] + E1[1] * c + E2[1] * s, pos[2] + E1[2] * c + E2[2] * s];
            this.rayDown.set(T([q[0] + D[0] * r, q[1] + D[1] * r, q[2] + D[2] * r]), T(D).multiplyScalar(-1).normalize());
            const h = this.rayDown.intersectObjects(this.meshes, false).find((x) => !(x.object.material.transparent && x.object.material.opacity < 0.5));
            const p3 = h ? h.point.clone() : T(pos);
            pts.push(p3.add(T(D).multiplyScalar(2)));
          }
          return pts;
        };
        this.ringOuter.geometry.dispose(); this.ringOuter.geometry = new THREE.BufferGeometry().setFromPoints(build(r));
        this.ringInner.geometry.dispose(); this.ringInner.geometry = new THREE.BufferGeometry().setFromPoints(build(r * 0.5));
      }
      this.invalidate();
    }
    /** Vorschau der Verformung: Kanten der neuen Oberfläche, ein Puffer wird wiederverwendet (Rechnung: app.sculptPreview) */
    setPreview(lines, invert) {
      if (!this.prevLines) {
        if (!lines || !lines.length) return;
        this.prevCap = 0;
        this.prevLines = new THREE.LineSegments(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color: 0x66ffa0, depthTest: false, transparent: true, opacity: 0.9 }));
        this.prevLines.renderOrder = 998; this.prevLines.frustumCulled = false;
        this.scene.add(this.prevLines);
      }
      const n = lines ? lines.length / 3 : 0;
      this.prevLines.visible = n > 0;
      this.prevLines.material.color.setHex(invert && this.app.sculptOpts.mode === 'push' ? 0xff8a5c : 0x66ffa0);   // Eindrücken (Umschalt) orange, Anheben grün
      if (n > 0) {
        const g = this.prevLines.geometry;
        if (n > this.prevCap) { this.prevCap = Math.max(n, this.prevCap * 2, 256); g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(this.prevCap * 3), 3)); }
        const a = g.getAttribute('position').array;
        for (let i = 0; i < n; i++) { a[i * 3] = lines[i * 3]; a[i * 3 + 1] = lines[i * 3 + 2]; a[i * 3 + 2] = -lines[i * 3 + 1]; }
        g.getAttribute('position').needsUpdate = true;
        g.setDrawRange(0, n);
      }
      this.invalidate();
    }
    updatePreview(pos, dir, invert, from) {
      this.hover = pos ? { pos: pos, dir: dir, from: from } : null;
      this.setPreview(pos ? this.app.sculptPreview(pos, invert, dir, from) : null, invert);
    }
    /** Ring und Vorschau folgen der Maus höchstens einmal je Bild und ruhen während eines Strichs (dab macht das dann selbst) */
    hoverRing(e) {
      this.hoverEv = { clientX: e.clientX, clientY: e.clientY, shiftKey: e.shiftKey };
      if (this.hoverRaf) return;
      this.hoverRaf = requestAnimationFrame(() => {
        this.hoverRaf = 0;
        const ev = this.hoverEv;
        if (!ev || !this.sculptOn || this.stroke) return;
        const tp = this.terrainPoint(ev.clientX, ev.clientY);
        this.showRing(tp && tp.pos, tp ? tp.dir : 0);
        this.updatePreview(tp && tp.pos, tp ? tp.dir : 0, ev.shiftKey, tp && tp.id);
      });
    }
    /** Erster Treffer auf formbares Gelände (Himmel, Decken, Trigger und andere Brushes davor werden übersprungen):
     *  { pos: Punkt in Map-Koordinaten, dir: Richtung der Oberfläche 0 +z, 1 −z, 2 +x, 3 −x, 4 +y, 5 −y } */
    terrainPoint(cx, cy) {
      // gleiche Mausposition, Kamera, Geometrie und Dokument → gleiches Ergebnis ohne neuen Raycast
      const cam = this.camera, key = [cx, cy, cam.position.x, cam.position.y, cam.position.z, cam.quaternion.x, cam.quaternion.y, cam.quaternion.z, cam.quaternion.w, cam.aspect, cam.fov].join();
      const tc = this.tpCache;
      if (tc && tc.key === key && tc.meshes === this.meshes && tc.doc === this.app.doc) return tc.res;
      const res = this.terrainPointRaw(cx, cy);
      this.tpCache = { key: key, meshes: this.meshes, doc: this.app.doc, res: res };
      return res;
    }
    terrainPointRaw(cx, cy) {
      const r = this.canvas.getBoundingClientRect(), ids = this.app.sculptIds();
      this.ray.setFromCamera(new THREE.Vector2(((cx - r.left) / r.width) * 2 - 1, -((cy - r.top) / r.height) * 2 + 1), this.camera);
      for (const h of this.ray.intersectObjects(this.meshes, false)) {
        const t = h.object.userData.tris, id = t && t[h.faceIndex * 2];
        if (id !== undefined && ids.has(id)) return { id: id, pos: [h.point.x, -h.point.z, h.point.y], dir: pickDir(h.face ? [h.face.normal.x, -h.face.normal.z, h.face.normal.y] : null) };
      }
      return null;
    }
    dab(e, first) {
      const tp = this.terrainPoint(e.clientX, e.clientY), pos = tp && tp.pos;
      if (!pos) { if (first) this.app.status(_('v3.no_terrain')); return; }
      // der erste wirksame Abdruck eines Strichs legt den Undo-Schritt an, die folgenden ersetzen ihn
      const fresh = first || !(this.stroke && this.stroke.began);
      const did = this.app.sculptAt(pos, e.shiftKey, fresh, tp.dir, tp.id);
      if (did && this.stroke) this.stroke.began = true;
      this.showRing(pos, tp.dir);
      this.updatePreview(pos, tp.dir, e.shiftKey, tp.id);
    }
    captures(key) { return this.ok && this.fly && /^[wasdqe]$/.test(key); }

    // ------------------------------------------------------------ Eingabe
    bindInput() {
      const c = this.canvas;
      c.tabIndex = 0;
      c.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        if (this.lastType === 'touch' && this.down && !this.down.moved) { this.down = null; this.app.contextAt(this, e); } // langes Tippen
      });
      c.addEventListener('pointerdown', (e) => {
        this.lastType = e.pointerType;
        this.app.activeView = this; this.app.views.forEach((v) => v.invalidate && v.invalidate());
        try { c.focus({ preventScroll: true }); } catch (err) { /* ignorieren */ }
        this.down = { x: e.clientX, y: e.clientY, button: e.button, moved: false, id: e.pointerId };
        if (this.sculptOn && !this.fly && e.button === 0 && !e.altKey) { this.stroke = { t: performance.now() }; this.busy(); try { c.setPointerCapture(e.pointerId); } catch (err) { /* ignorieren */ } this.dab(e, true); }
        if (this.fly && e.button === 0) { try { c.setPointerCapture(e.pointerId); } catch (err) { /* ignorieren */ } }
      });
      c.addEventListener('pointermove', (e) => {
        if (this.sculptOn && !this.fly && e.pointerType !== 'touch' && !(this.down && this.down.button === 2)) this.hoverRing(e);
        const d = this.down; if (!d || d.id !== e.pointerId) return;
        if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > 4) d.moved = true;
        if (this.stroke && performance.now() - this.stroke.t > 70) { this.stroke.t = performance.now(); this.dab(e, false); }
        if (this.fly && d.button === 0) {
          const dx = e.movementX || 0, dy = e.movementY || 0;
          this.yaw += dx * 0.0035; this.pitch = Math.max(-1.5, Math.min(1.5, this.pitch - dy * 0.0035));
          this.applyLook(); this.busy(); this.invalidate();
        }
      });
      c.addEventListener('pointerup', (e) => {
        const d = this.down; this.down = null;
        if (this.stroke) { this.stroke = null; this.app.sculptEnd(); return; }
        if (!d || d.id !== e.pointerId || d.moved) return;
        if (d.button === 2) { this.app.contextAt(this, e); return; }
        if (d.button !== 0) return;
        this.pick(e);
      });
      c.addEventListener('pointerleave', () => { if (this.sculptOn && !this.stroke) { this.hoverEv = null; this.hover = null; this.showRing(null); this.setPreview(null); } });
      c.addEventListener('wheel', (e) => {
        if (!this.fly) return;
        e.preventDefault();
        this.flySpeed = Math.max(60, Math.min(4000, this.flySpeed * (e.deltaY < 0 ? 1.15 : 0.87)));
        this.app.status(_('v3.fly_speed', { v: Math.round(this.flySpeed) }));
      }, { passive: false });
      // Umschalt gedrückt/losgelassen ohne Mausbewegung: Vorschau (anheben ↔ eindrücken) sofort umschalten
      const shiftPreview = (e) => { if (e.key === 'Shift' && this.sculptOn && this.hover) this.updatePreview(this.hover.pos, this.hover.dir, e.type === 'keydown', this.hover.from); };
      window.addEventListener('keydown', shiftPreview);
      window.addEventListener('keyup', shiftPreview);
      window.addEventListener('keydown', (e) => {
        if (!this.fly || this.app.activeView !== this || /^(INPUT|TEXTAREA|SELECT)$/.test((e.target || {}).tagName || '')) return;
        const k = e.key.toLowerCase();
        if (k === 'shift' || /^[wasdqe]$/.test(k)) { this.keys.add(k); e.preventDefault(); this.invalidate(); }
      });
      window.addEventListener('keyup', (e) => { this.keys.delete(e.key.toLowerCase()); });
      window.addEventListener('blur', () => this.keys.clear());
    }

    /** Strahl durch Bildschirmpunkt (clientX/Y). Liefert { best: nächster Treffer | null, pos: Punkt in Map-Koordinaten | null }.
     *  Ohne Treffer wird die waagerechte Ebene auf Höhe app.entDepth[2] geschnitten. */
    rayHit(cx, cy, alt) {
      const r = this.canvas.getBoundingClientRect(), app = this.app;
      const ndc = new THREE.Vector2(((cx - r.left) / r.width) * 2 - 1, -((cy - r.top) / r.height) * 2 + 1);
      this.ray.setFromCamera(ndc, this.camera);
      let best = null;
      const eh = this.ray.intersectObjects(this.entMeshes, false)[0];
      if (eh) best = { dist: eh.distance, type: 'e', id: eh.object.userData.entId, point: eh.point.clone() };
      this.ray.intersectObjects(this.meshes, false).forEach((h) => {
        if (best && h.distance >= best.dist) return;
        const mat = h.object.material;
        if (mat.transparent && mat.opacity < 0.5 && !alt) return; // durchsichtige Spezialflächen nicht anklickbar, außer mit Alt
        const t = h.object.userData.tris, i = h.faceIndex * 2;
        if (t[i] === undefined) return;
        best = { dist: h.distance, type: 'b', id: t[i], face: t[i + 1], point: h.point.clone(), normal: h.face ? [h.face.normal.x, -h.face.normal.z, h.face.normal.y] : null };
      });
      let pos = null;
      if (best) pos = [best.point.x, -best.point.z, best.point.y];
      else {
        const p = new THREE.Vector3();
        if (this.ray.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), -app.entDepth[2]), p)) pos = [p.x, -p.z, p.y];
      }
      return { best: best, pos: pos };
    }

    /** Werkzeug „Entity“: Klick setzt die gewählte Punkt-Entity auf die angeklickte Fläche (Box liegt auf, nicht im Brush) */
    placeAt(e) {
      const app = this.app, hit = this.rayHit(e.clientX, e.clientY, e.altKey);
      let o = null;
      if (hit.best && hit.best.type === 'b') {
        const d = this.ray.ray.direction, n = faceNormal(hit.best.normal, [d.x, -d.z, d.y]);
        o = originOnSurface([hit.best.point.x, -hit.best.point.z, hit.best.point.y], n, EntDefs.get(app.entClass).size || null);
      } else if (hit.pos) { // ohne Fläche: auf die Ebene in der Tiefe der Entity, aufs Raster gerundet
        const g = app.snapOn ? app.grid : 1;
        o = hit.pos.map((v) => Math.round(v / g) * g);
      }
      if (o) app.placeEntity(o); else app.status(_('v3.no_surface'));
    }

    pick(e) {
      const app = this.app;
      if (app.tool === 'entity' && !EntDefs.isBrushClass(app.entClass) && !(e.shiftKey || e.ctrlKey || e.metaKey)) { this.placeAt(e); return; }
      const best = this.rayHit(e.clientX, e.clientY, e.altKey).best;
      if (app.tool === 'texture' && app.texMode === 'paint') { // Anmalen: Klick malt die Fläche an, Alt+Klick nimmt sie auf
        if (best && best.type === 'b') { if (e.altKey) app.pickTexture(best.id, best.face); else app.paintFace(best.id, best.face); }
        return;
      }
      const additive = e.shiftKey || e.ctrlKey || e.metaKey;
      if (!best) { if (!additive) app.setSel(Cmd.emptySel()); return; }
      const surface = app.tool === 'texture' && app.texPaint.scope === 'face' && best.type === 'b'; // Textur-Modus „Fläche“: Klick wählt eine Oberfläche
      const ns = additive ? Cmd.cloneSel(app.sel) : Cmd.emptySel();
      if (best.type === 'e') {
        if (additive && ns.e.has(best.id)) ns.e.delete(best.id); else ns.e.add(best.id);
        app.selFace = null; app.setSel(ns);
      } else if (e.altKey || surface) {
        if (additive) {
          const cur = app.faceSel.slice(), i = cur.findIndex((x) => x.b === best.id && x.f === best.face);
          if (i >= 0) cur.splice(i, 1); else cur.push({ b: best.id, f: best.face });
          app.setFaceSel(cur);
        } else app.setFaceSel([{ b: best.id, f: best.face }]);
      } else {
        if (additive && ns.b.has(best.id)) ns.b.delete(best.id); else ns.b.add(best.id);
        app.selFace = null; app.setSel(ns);
      }
    }
  }
  return View3D;
})(Core, Cmd, EntDefs, Light, Doors);
export default View3D;
